// libs
import React, { useEffect } from 'react'

// helpers
import { toAppLocation } from 'src/utils/location'
import { StoredLocationHelper } from 'src/utils/location_storage/location_storage'
import { UrlUtils } from 'src/utils/url/url'

// graphql
import { AreaQuery, useArea } from '@lopo-tech/lopo-api'
import { Params } from '@sentry/react/build/types/types'
import { useParams } from 'react-router'
import { useChangeAppLocation } from 'src/actions/app-state/app-state_hooks'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { FRONTEND_NOT_FOUND_PATH } from '../../../constants/urls'
import GoogleTagManager from '../../../utils/reporting/google-tag-manager'

export type LocationSlugNotFound = 'lookup-failed' | 'unknown-slug' | 'empty-area'

interface IAreaLookup {
  data?: AreaQuery;
  loading: boolean;
  error?: { name: string };
}

// the API answers an unknown slug with `area: null`, not with an error
export const getLocationSlugNotFound = (
  { data, loading, error }: IAreaLookup
): LocationSlugNotFound | undefined => {
  if (error) {
    return error.name === 'Invariant Violation' ? undefined : 'lookup-failed'
  }
  if (loading) {
    return undefined
  }
  if (data?.area === null) {
    return 'unknown-slug'
  }
  if (data?.area && toAppLocation(data.area) === undefined) {
    return 'empty-area'
  }
  return undefined
}

// the event action tells a mistyped or removed slug apart from an unusable cluster and from a failed request
export const getNotFoundTrackingAction = (notFound: LocationSlugNotFound): string => {
  switch (notFound) {
    case 'unknown-slug':
      return 'unknown_slug_called'
    case 'empty-area':
      return 'empty_area_called'
    case 'lookup-failed':
      return 'slug_lookup_failed'
  }
}

// the tabs and the not-found page link to the stored location, so an unknown one would lead back here
export const shouldForgetStoredLocation = (
  notFound: LocationSlugNotFound | undefined,
  storedLocationSlug: string | undefined,
  routeLocationSlug: string
): boolean => (notFound === 'unknown-slug' || notFound === 'empty-area') && storedLocationSlug === routeLocationSlug

export const LocationSlugHandler: React.FC<React.PropsWithChildren<object>> = ({ children }) => {
  const { locationSlug: routeLocationSlug = '' } = useParams<Params>()

  const locationShape = useGetAppLocationShape()
  const changeAppLocation = useChangeAppLocation()

  const { data, loading, error } = useArea({
    variables: { slug: routeLocationSlug }
  })
  const notFound = getLocationSlugNotFound({ data, loading, error })

  useEffect(() => {
    if (!data?.area || loading) return

    const appLocation = toAppLocation(data.area)
    if (appLocation && appLocation.id !== locationShape?.id) {
      changeAppLocation(appLocation)
    }
  }, [
    data?.area,
    changeAppLocation,
    loading,
    error
  ])

  useEffect(() => {
    if (!notFound) return

    if (shouldForgetStoredLocation(notFound, StoredLocationHelper.getLastLocation()?.slug, routeLocationSlug)) {
      StoredLocationHelper.removeLastLocation()
    }
    GoogleTagManager.pushEventWithValue(
      'API_ERROR',
      getNotFoundTrackingAction(notFound),
      routeLocationSlug
    )
    UrlUtils.replaceLocationHref(FRONTEND_NOT_FOUND_PATH)
  }, [notFound])

  return (
    <>
      {children}
    </>
  )
}

export default LocationSlugHandler
